'use es6';

import { buildCalculateHoverPositions } from 'layout-dnd-utils/hoverPositions/hoverPositionCalculator';
import calculateLayoutTreeHoverPositions from 'layout-dnd-utils/hoverPositions/calculateLayoutTreeHoverPositions';
import calculateGridLayoutTreeHoverPositions from 'layout-dnd-utils/hoverPositions/calculateGridLayoutTreeHoverPositions';
import { isPositionWithinBounds } from 'layout-dnd-utils/geometry/basic';
import { hoverTypes } from 'layout-dnd-utils/constants/dndAreaHoverPositionConstants';
import calculateBasicHoverPositions from 'layout-dnd-utils/hoverPositions/calculateBasicHoverPositions';
import { GLOBAL_GROUPS_CONFIG, GLOBAL_PARTIALS_CONFIG, HEADER_INFO_CONFIG, FOOTER_INFO_CONFIG, STATIC_MODULES_CONFIG, FLEX_COLUMN_MODULES_CONFIG, LAYOUT_SECTIONS_CONFIG, POST_BODY_MODULES_CONFIG, NESTED_MODULES_IN_RTE_CONFIG, GRID_LAYOUTS_NODE_POSITION_CONFIG } from 'InpageEditorUI/lib/layoutSection/constants';
import { filterObjectValues, deepFlattenArraysInObject } from 'ContentEditorUI/lib/collectionHelpers';
import { layoutNodeTypes } from 'layout-dnd-utils/constants/dndAreaHoverPositionConstants';
const wrapWithCheckForMouseInsideSelectedItemBounds = origCalcHoverPositionsFunction => {
  return (nodePositions, adjustedMouseCoords, config) => {
    const {
      selectedItemPositionInfo,
      getNodePositionsSubset
    } = config;
    if (selectedItemPositionInfo && isPositionWithinBounds(adjustedMouseCoords, selectedItemPositionInfo)) {
      const newConfig = Object.assign({}, config, {
        getNodePositionsSubset: nodePositionsInput => {
          return [...getNodePositionsSubset(nodePositionsInput), Object.assign({}, selectedItemPositionInfo)];
        }
      });
      return origCalcHoverPositionsFunction(nodePositions, adjustedMouseCoords, newConfig);
    }
    return origCalcHoverPositionsFunction(nodePositions, adjustedMouseCoords, config);
  };
};
const calculateBasicHoverPositionsPlus = wrapWithCheckForMouseInsideSelectedItemBounds(calculateBasicHoverPositions);
const isFixedOrAbsoluteNodePosition = nodePosition => nodePosition.isFixedPosition || nodePosition.isAbsolutePosition;
const isNotFixedOrAbsoluteNodePosition = nodePosition => !isFixedOrAbsoluteNodePosition(nodePosition);
const isFixedOrAbsoluteGridNodePosition = nodePosition => {
  if (nodePosition.type === layoutNodeTypes.GRID_LAYOUT_MODULE) {
    return nodePosition.isFixedPosition || nodePosition.isAbsolutePosition;
  }
  return false;
};
const isNotFixedOrAbsoluteGridNodePosition = nodePosition => {
  return !isFixedOrAbsoluteGridNodePosition(nodePosition);
};
const fixedAndAbsoluteConfig = {
  getCustomFields: nodePosition => {
    const {
      isModule,
      isStaticModule,
      isFlexColumnModule,
      isGlobalGroup,
      isGlobalPartial,
      isFixedPosition,
      isAbsolutePosition,
      layoutSectionId,
      hasDelegatedBounds
    } = nodePosition;
    return {
      isModule,
      isStaticModule,
      isFlexColumnModule,
      isGlobalGroup,
      isGlobalPartial,
      isFixedPosition,
      isAbsolutePosition,
      layoutSectionId,
      hasDelegatedBounds,
      type: hoverTypes.IN_RECTANGLE_BOUNDS
    };
  },
  calculateHoverPositions: calculateBasicHoverPositionsPlus,
  getNodePositionsSubset: nodePositionObj => {
    const fixedAndAbsoluteNodePositions = deepFlattenArraysInObject(nodePositionObj).filter(isFixedOrAbsoluteNodePosition);
    return fixedAndAbsoluteNodePositions;
  }
};
const filterFixedOrAbsoluteGridLayoutNodePositions = nodePositionsArrayByGridId => {
  const result = {};
  Object.keys(nodePositionsArrayByGridId).forEach(key => {
    const value = nodePositionsArrayByGridId[key];
    result[key] = value.filter(isNotFixedOrAbsoluteGridNodePosition);
  });
  return result;
};
const staticModuleConfig = {
  getCustomFields: nodePosition => ({
    isModule: true,
    isStaticModule: true,
    isStaticSectionModule: nodePosition.isStaticSectionModule,
    hasDelegatedBounds: nodePosition.hasDelegatedBounds,
    type: hoverTypes.IN_RECTANGLE_BOUNDS
  }),
  calculateHoverPositions: calculateBasicHoverPositionsPlus,
  getNodePositionsSubset: nodePositions => {
    const staticPositions = nodePositions[STATIC_MODULES_CONFIG];
    return staticPositions ? staticPositions.filter(isNotFixedOrAbsoluteNodePosition) : [];
  },
  debug: true
};
const postBodyModuleConfig = {
  getCustomFields: nodePosition => ({
    isModule: true,
    isStaticSectionModule: nodePosition.isStaticSectionModule,
    isStaticModule: false,
    isPostBodyModule: true,
    hasDelegatedBounds: nodePosition.hasDelegatedBounds,
    type: hoverTypes.IN_RECTANGLE_BOUNDS
  }),
  calculateHoverPositions: calculateBasicHoverPositionsPlus,
  getNodePositionsSubset: nodePositions => {
    const positions = nodePositions[POST_BODY_MODULES_CONFIG];
    return positions ? positions.filter(isNotFixedOrAbsoluteNodePosition) : [];
  },
  debug: true
};
const nestedModuleInRichTextConfig = {
  getCustomFields: nodePosition => ({
    isModule: true,
    isStaticSectionModule: nodePosition.isStaticSectionModule,
    isStaticModule: false,
    isModuleInRichText: true,
    hasDelegatedBounds: nodePosition.hasDelegatedBounds,
    type: hoverTypes.IN_RECTANGLE_BOUNDS
  }),
  calculateHoverPositions: calculateBasicHoverPositionsPlus,
  getNodePositionsSubset: nodePositions => {
    const positions = nodePositions[NESTED_MODULES_IN_RTE_CONFIG];
    return positions ? positions.filter(isNotFixedOrAbsoluteNodePosition) : [];
  },
  debug: true
};
const flexColumnModuleConfig = {
  getCustomFields: nodePosition => ({
    isModule: true,
    isFlexColumnModule: true,
    hasDelegatedBounds: nodePosition.hasDelegatedBounds,
    type: hoverTypes.IN_RECTANGLE_BOUNDS
  }),
  calculateHoverPositions: calculateBasicHoverPositionsPlus,
  getNodePositionsSubset: nodePositions => {
    const flexColPositions = nodePositions[FLEX_COLUMN_MODULES_CONFIG];
    return flexColPositions ? flexColPositions.filter(isNotFixedOrAbsoluteNodePosition) : [];
  },
  debug: true
};
const globalGroupConfig = {
  getCustomFields: nodePosition => ({
    isGlobalGroup: true,
    hasDelegatedBounds: nodePosition.hasDelegatedBounds,
    type: hoverTypes.IN_RECTANGLE_BOUNDS,
    uniquePositionId: nodePosition.uniquePositionId
  }),
  calculateHoverPositions: calculateBasicHoverPositionsPlus,
  getNodePositionsSubset: nodePositions => {
    const positions = nodePositions[GLOBAL_GROUPS_CONFIG];
    return positions ? positions.filter(isNotFixedOrAbsoluteNodePosition) : [];
  }
};
const globalPartialConfig = {
  getCustomFields: nodePosition => ({
    isGlobalPartial: true,
    isHeader: false,
    isFooter: false,
    hasDelegatedBounds: nodePosition.hasDelegatedBounds,
    type: hoverTypes.IN_RECTANGLE_BOUNDS,
    uniquePositionId: nodePosition.uniquePositionId
  }),
  calculateHoverPositions: calculateBasicHoverPositionsPlus,
  getNodePositionsSubset: nodePositions => {
    const positions = nodePositions[GLOBAL_PARTIALS_CONFIG];
    return positions ? positions.filter(isNotFixedOrAbsoluteNodePosition) : [];
  }
};
const headerConfig = {
  getCustomFields: nodePosition => ({
    isGlobalPartial: true,
    isHeader: true,
    isFooter: false,
    hasDelegatedBounds: nodePosition.hasDelegatedBounds,
    type: hoverTypes.IN_RECTANGLE_BOUNDS,
    uniquePositionId: nodePosition.uniquePositionId
  }),
  calculateHoverPositions: calculateBasicHoverPositionsPlus,
  getNodePositionsSubset: nodePositions => {
    const positions = nodePositions[HEADER_INFO_CONFIG];
    return positions ? positions.filter(isNotFixedOrAbsoluteNodePosition) : [];
  }
};
const footerConfig = {
  getCustomFields: nodePosition => ({
    isGlobalPartial: true,
    isHeader: false,
    isFooter: true,
    hasDelegatedBounds: nodePosition.hasDelegatedBounds,
    type: hoverTypes.IN_RECTANGLE_BOUNDS,
    uniquePositionId: nodePosition.uniquePositionId
  }),
  calculateHoverPositions: calculateBasicHoverPositionsPlus,
  getNodePositionsSubset: nodePositions => {
    const positions = nodePositions[FOOTER_INFO_CONFIG];
    return positions ? positions.filter(isNotFixedOrAbsoluteNodePosition) : [];
  }
};
const layoutSectionConfig = {
  getCustomFields: nodePosition => ({
    hasDelegatedBounds: nodePosition.hasDelegatedBounds
  }),
  calculateHoverPositions: wrapWithCheckForMouseInsideSelectedItemBounds(calculateLayoutTreeHoverPositions),
  getNodePositionsSubset: nodePositions => filterObjectValues(nodePositions[LAYOUT_SECTIONS_CONFIG], isNotFixedOrAbsoluteNodePosition)
};
const gridLayoutConfig = {
  getCustomFields: nodePosition => ({
    hasDelegatedBounds: nodePosition.hasDelegatedBounds
  }),
  calculateHoverPositions: calculateGridLayoutTreeHoverPositions,
  getNodePositionsSubset: nodePositions => {
    return filterFixedOrAbsoluteGridLayoutNodePositions(nodePositions[GRID_LAYOUTS_NODE_POSITION_CONFIG]);
  }
};
const orderedHoverPositionConfig = [postBodyModuleConfig, nestedModuleInRichTextConfig, fixedAndAbsoluteConfig, globalGroupConfig, globalPartialConfig, staticModuleConfig, flexColumnModuleConfig, layoutSectionConfig];
const orderedHoverPositionConfigForGridLayouts = [globalGroupConfig, globalPartialConfig, headerConfig, footerConfig, gridLayoutConfig];
const orderedHoverPositionConfigForDecoupledHeadersAndFooters = [postBodyModuleConfig, nestedModuleInRichTextConfig, fixedAndAbsoluteConfig, globalGroupConfig, globalPartialConfig, headerConfig, footerConfig, staticModuleConfig, flexColumnModuleConfig, layoutSectionConfig];
export const calculateHoverPositions = buildCalculateHoverPositions(orderedHoverPositionConfig);
export const calculateHoverPositionsForGridLayouts = buildCalculateHoverPositions(orderedHoverPositionConfigForGridLayouts);
export const calculateHoverPositionsForDecoupledHeadersAndFooters = buildCalculateHoverPositions(orderedHoverPositionConfigForDecoupledHeadersAndFooters);